import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import { RichEditor } from '../components/RichEditor';
import { useAuth } from '../context/AuthContext';

// ── IST (Asia/Kolkata, UTC+5:30) date/time helpers ─────────────────────────────
// CRITICAL: This platform is India-only. We do NOT rely on the browser's or
// server's OS timezone for exam start/end times — that caused times to silently
// shift to "server time" (often UTC) whenever the app ran on a machine whose
// system clock wasn't set to IST. All exam scheduling is always interpreted
// and displayed as IST, regardless of where the browser/server physically runs.
const IST_TZ = 'Asia/Kolkata';
const IST_OFFSET_MS = (5 * 60 + 30) * 60000; // India has no DST — fixed offset is safe

function getISTParts(dateOrIso) {
  const d = typeof dateOrIso === 'string' ? new Date(dateOrIso) : dateOrIso;
  if (!d || isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TZ, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const parts = {};
  fmt.formatToParts(d).forEach(p => { parts[p.type] = p.value; });
  return parts;
}

// UTC ISO string (from backend) → "YYYY-MM-DDTHH:mm" IST wall-clock for datetime-local value=
const toLocalInput = (isoUTC) => {
  if (!isoUTC) return '';
  const p = getISTParts(isoUTC);
  if (!p) return '';
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// "now" expressed in IST, for the min= attribute on date pickers
const nowLocalInput = () => toLocalInput(new Date().toISOString());

// datetime-local's "YYYY-MM-DDTHH:mm" (entered by the user AS IST) → correct UTC ISO string
const istInputToUTC = (localValue) => {
  if (!localValue) return null;
  const [datePart, timePart] = localValue.split('T');
  const [y, mo, da] = datePart.split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  const utcMs = Date.UTC(y, mo - 1, da, h, mi) - IST_OFFSET_MS;
  return new Date(utcMs).toISOString();
};

const EMPTY = {
  title: '', description: '', instructions: '',
  duration_minutes: 60, total_marks: 100, passing_marks: 40,
  max_warnings: 3, min_submit_minutes: 30,
  proctoring_enabled: true, require_fullscreen: true, shuffle_questions: true,
  start_time: '', end_time: '', disabled_violations: [],
};

// All violation types the AI/browser proctoring can detect.
// Unchecked = student still sees a warning toast, but it does NOT count toward
// total_warnings / trust_score / auto-termination.
const VIOLATION_TYPES = [
  { group: '📷 Camera / AI Detection', items: [
    { id: 'face_not_detected',  label: 'Face Not Detected',        hint: 'Student not visible / camera covered / too far away' },
    { id: 'multiple_faces',     label: 'Multiple Faces',           hint: 'More than one person detected in frame' },
    { id: 'looking_away',       label: 'Looking Away',             hint: 'Student not facing the camera consistently' },
    { id: 'suspicious_movement',label: 'Suspicious Movement',      hint: 'Unusual high motion detected' },
  ]},
  { group: '🖥️ Browser / Screen', items: [
    { id: 'tab_switch',         label: 'Tab Switch / Minimize',    hint: 'Student switched tabs or minimized browser' },
    { id: 'window_blur',        label: 'Window Lost Focus',        hint: 'Student clicked outside the exam window' },
    { id: 'full_screen_exit',   label: 'Fullscreen Exit',          hint: 'Student exited fullscreen mode' },
    { id: 'window_focus_no_fullscreen', label: 'Returned Without Fullscreen', hint: 'Came back to exam but not in fullscreen' },
    { id: 'copy_paste',         label: 'Copy / Paste',             hint: 'Copy or paste attempted' },
    { id: 'right_click',        label: 'Right-Click',              hint: 'Right-click context menu attempted' },
    { id: 'keyboard_shortcut',  label: 'Suspicious Shortcut',      hint: 'Dev tools / print-screen / alt-tab attempted' },
  ]},
];

export default function CreateExamPage() {
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const [form,    setForm]    = useState({ ...EMPTY });
  const [saving,  setSaving]  = useState(false);

  if (user?.role !== 'organization') {
    navigate('/manage/exams');
    return null;
  }

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked
              : e.target.type === 'number'   ? (parseInt(e.target.value) || 0)
              : e.target.value;
    setForm(p => ({ ...p, [key]: val }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      // start_time/end_time are already proper UTC ISO strings (converted at
      // input time via istInputToUTC) — no further conversion needed here.
      const res = await axios.post('/api/exams', form);
      toast.success('Exam created successfully!');
      navigate('/manage/exams');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create exam');
    } finally { setSaving(false); }
  };

  const fields = [
    { label: 'Duration (minutes)', key: 'duration_minutes',   type: 'number', min: 5 },
    { label: 'Total Marks',        key: 'total_marks',        type: 'number', min: 1 },
    { label: 'Passing Marks',      key: 'passing_marks',      type: 'number', min: 0 },
    { label: 'Max Violations',     key: 'max_warnings',       type: 'number', min: 0 },
    { label: 'Min Submit (min)',    key: 'min_submit_minutes', type: 'number', min: 0 },
  ];

  const toggles = [
    { label: '🔒 AI Proctoring (camera & mic)',  key: 'proctoring_enabled'  },
    { label: '⛶ Require Fullscreen',             key: 'require_fullscreen'  },
    { label: '🔀 Shuffle Questions',              key: 'shuffle_questions'   },
  ];

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">
        {/* Page header */}
        <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h1 className="page-title">➕ Create New Exam</h1>
            <p className="page-subtitle">Fill in the details below to create a new exam</p>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/manage/exams')}>← Back to Exams</button>
        </div>

        <div style={{ maxWidth: 760 }}>

          {/* Basic info */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>📝 Basic Information</h3>

            <div className="form-group">
              <label className="form-label">Exam Title <span style={{ color:'var(--accent-danger)' }}>*</span></label>
              <input className="form-input" value={form.title}
                onChange={set('title')} placeholder="e.g. Computer Science Mid Term 2025" autoFocus />
            </div>

            <div className="form-group">
              <label className="form-label">Description <span style={{ fontSize:11, color:'var(--text-muted)' }}>(optional)</span></label>
              <textarea className="form-input form-textarea" style={{ minHeight: 80 }}
                value={form.description} onChange={set('description')}
                placeholder="Short description of this exam..." />
            </div>

            <div className="form-group">
              <label className="form-label">
                Instructions for Students <span style={{ fontSize:11, color:'var(--text-muted)' }}>(optional — shown before exam starts)</span>
              </label>
              <RichEditor
                value={form.instructions}
                onChange={html => setForm(p => ({ ...p, instructions: html }))}
                placeholder="Enter instructions in Hindi or English... e.g. Read all questions carefully before answering. सभी प्रश्नों को ध्यान से पढ़ें।"
                minHeight={140}
              />
            </div>
          </div>

          {/* Numeric settings */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>⚙ Exam Settings</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {fields.map(f => (
                <div key={f.key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <input className="form-input" type="number" min={f.min}
                    value={form[f.key]} onChange={set(f.key)} />
                </div>
              ))}
            </div>
          </div>

          {/* Schedule — always interpreted as IST (India), regardless of server/browser timezone */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>📅 Schedule <span style={{ fontSize:12, fontWeight:400, color:'var(--text-muted)' }}>(optional)</span></h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div className="form-group">
                <label className="form-label">Start Date & Time <span style={{ fontSize:11, fontWeight:700, color:'var(--accent-primary)' }}>(IST)</span></label>
                <input className="form-input" type="datetime-local" min={nowLocalInput()}
                  value={toLocalInput(form.start_time)}
                  onChange={e => setForm(p => ({ ...p, start_time: istInputToUTC(e.target.value) }))} />
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Students cannot start before this time</div>
              </div>
              <div className="form-group">
                <label className="form-label">End Date & Time <span style={{ fontSize:11, fontWeight:700, color:'var(--accent-primary)' }}>(IST)</span></label>
                <input className="form-input" type="datetime-local" min={nowLocalInput()}
                  value={toLocalInput(form.end_time)}
                  onChange={e => setForm(p => ({ ...p, end_time: istInputToUTC(e.target.value) }))} />
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>Students cannot start after this time</div>
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>🔧 Options</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {toggles.map(t => (
                <label key={t.key} style={{ display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                  <input type="checkbox" checked={!!form[t.key]} onChange={set(t.key)}
                    style={{ width:18, height:18, accentColor:'var(--accent-primary)', cursor:'pointer', flexShrink:0 }} />
                  <span style={{ fontSize:14, fontWeight:500 }}>{t.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Violation Type Settings */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>🚨 Violation Settings</h3>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Uncheck any violation type that should only show a warning to the student
              without counting toward their violation limit or trust score. Useful for
              environments where false positives (e.g. "face too far") are common.
            </p>
            {VIOLATION_TYPES.map(group => (
              <div key={group.group} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>{group.group}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {group.items.map(v => {
                    const isCounted = !form.disabled_violations.includes(v.id);
                    return (
                      <label key={v.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '8px 10px', borderRadius: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
                        <input type="checkbox" checked={isCounted}
                          onChange={e => setForm(p => ({
                            ...p,
                            disabled_violations: e.target.checked
                              ? p.disabled_violations.filter(x => x !== v.id)
                              : [...p.disabled_violations, v.id],
                          }))}
                          style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--accent-primary)', cursor: 'pointer', flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{v.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v.hint}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div style={{ display:'flex', gap:12, justifyContent:'flex-end', paddingBottom:40 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/manage/exams')} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}
              style={{ minWidth:160, justifyContent:'center' }}>
              {saving ? '⏳ Creating...' : '✅ Create Exam'}
            </button>
          </div>

        </div>
      </main>
    </div>
  );
}
